import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { Store, MapPin, Calendar, FileText } from 'lucide-react';
import ApiService from '../../services/api';

interface BasicInfoData {
  merchant_id: number;
  city_id: number;
  branch_id?: number;
  title: string;
  description?: string;
  start_date: string;
  end_date: string;
}

interface BasicInfoStepProps {
  data: BasicInfoData | null;
  onUpdate: (data: BasicInfoData) => void;
  onNext: () => void;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({ data, onUpdate, onNext }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [merchants, setMerchants] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  
  const { register, handleSubmit, formState: { errors }, watch } = useForm<BasicInfoData>({
    defaultValues: data || {}
  });

  // جلب بيانات المتاجر والمدن من الـ API
  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        const [merchantsData, citiesData] = await Promise.all([
          ApiService.getMerchants(),
          ApiService.getCities()
        ]);
        setMerchants(merchantsData);
        setCities(citiesData);
      } catch (err) {
        setError('حدث خطأ أثناء جلب البيانات');
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  const selectedMerchant = watch('merchant_id');
  
  // جلب فروع المتجر المحدد
  useEffect(() => {
    const fetchBranches = async () => {
      if (selectedMerchant) {
        try {
          const branchesData = await ApiService.getMerchantBranches(selectedMerchant);
          setBranches(branchesData);
        } catch (err) {
          setBranches([]);
        }
      } else {
        setBranches([]);
      }
    };
    fetchBranches();
  }, [selectedMerchant]);

  const onSubmit = async (formData: BasicInfoData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.createOffer({
        title: formData.title,
        description: formData.description || null,
        merchant_id: parseInt(formData.merchant_id.toString()),
        city_id: parseInt(formData.city_id.toString()),
        branch_id: formData.branch_id ? parseInt(formData.branch_id.toString()) : null,
        start_date: formData.start_date + "T00:00:00", // تحويل إلى datetime
        end_date: formData.end_date + "T23:59:59", // تحويل إلى datetime
      });
      onUpdate({ ...formData, offer_id: response.id });
      onNext();
    } catch (e) {
      setError('حدث خطأ أثناء إنشاء العرض');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-8"
      >
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-lg">جاري تحميل البيانات...</div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-8"
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            معلومات العرض الأساسية
          </h2>
          <p className="text-gray-600">
            ابدأ بتحديد تفاصيل العرض الجديد
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Merchant Selection */}
          <div className="form-group">
            <label className="form-label flex items-center space-x-2 space-x-reverse">
              <Store className="w-5 h-5 text-gray-500" />
              <span>اختر الهايبرماركت *</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {merchants.map((merchant) => (
                <label key={merchant.id} className="cursor-pointer">
                  <input
                    type="radio"
                    value={merchant.id}
                    {...register('merchant_id', { required: 'يجب اختيار الهايبرماركت' })}
                    className="sr-only"
                  />
                  <div className={`
                    p-4 border-2 rounded-lg transition-all duration-200 text-center hover:border-primary-300
                    ${watch('merchant_id') == merchant.id 
                      ? 'border-primary-500 bg-primary-50' 
                      : 'border-gray-200 bg-white'
                    }
                  `}>
                    <div className="w-12 h-12 bg-gray-100 rounded-lg mx-auto mb-2 flex items-center justify-center">
                      <Store className="w-6 h-6 text-gray-500" />
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {merchant.name_ar || merchant.name}
                    </span>
                  </div>
                </label>
              ))}
            </div>
            {errors.merchant_id && (
              <span className="form-error">{errors.merchant_id.message}</span>
            )}
          </div>

          {/* City and Branch */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-group">
              <label className="form-label flex items-center space-x-2 space-x-reverse">
                <MapPin className="w-5 h-5 text-gray-500" />
                <span>المدينة *</span>
              </label>
              <select
                {...register('city_id', { required: 'يجب اختيار المدينة' })}
                className={`input ${errors.city_id ? 'input-error' : ''}`}
              >
                <option value="">اختر المدينة</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name_ar || city.name}
                  </option>
                ))}
              </select>
              {errors.city_id && (
                <span className="form-error">{errors.city_id.message}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">الفرع (اختياري)</label>
              <select
                {...register('branch_id')}
                className="input"
                disabled={!selectedMerchant}
              >
                <option value="">جميع الفروع</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name_ar || branch.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Title and Description */}
          <div className="space-y-6">
            <div className="form-group">
              <label className="form-label flex items-center space-x-2 space-x-reverse">
                <FileText className="w-5 h-5 text-gray-500" />
                <span>عنوان العرض *</span>
              </label>
              <input
                type="text"
                {...register('title', { required: 'يجب إدخال عنوان العرض' })}
                className={`input ${errors.title ? 'input-error' : ''}`}
                placeholder="مثال: عروض نهاية الأسبوع الكبرى"
              />
              {errors.title && (
                <span className="form-error">{errors.title.message}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">وصف العرض (اختياري)</label>
              <textarea
                {...register('description')}
                rows={3}
                className="input"
                placeholder="وصف مختصر للعرض..."
              />
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-group">
              <label className="form-label flex items-center space-x-2 space-x-reverse">
                <Calendar className="w-5 h-5 text-gray-500" />
                <span>تاريخ البداية *</span>
              </label>
              <input
                type="date"
                {...register('start_date', { required: 'يجب تحديد تاريخ البداية' })}
                className={`input ${errors.start_date ? 'input-error' : ''}`}
              />
              {errors.start_date && (
                <span className="form-error">{errors.start_date.message}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label flex items-center space-x-2 space-x-reverse">
                <Calendar className="w-5 h-5 text-gray-500" />
                <span>تاريخ النهاية *</span>
              </label>
              <input
                type="date"
                {...register('end_date', { required: 'يجب تحديد تاريخ النهاية' })}
                className={`input ${errors.end_date ? 'input-error' : ''}`}
              />
              {errors.end_date && (
                <span className="form-error">{errors.end_date.message}</span>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-6">
            <button type="submit" onClick={handleSubmit(onSubmit)} disabled={loading} className="btn-primary">
              {loading ? 'جاري الحفظ...' : 'التالي'}
            </button>
            {error && <div className="text-red-600 mt-2">{error}</div>}
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default BasicInfoStep;